import { DataBadges, PageBanner, Section, SectionTitle, useApi } from '@nanning/ui';
import { assetUrl, useT } from '../i18n';
import { creditOf, findImage, useSiteData } from '../useSiteData';
import type { FinancialsApi, FundraisingApi } from '../types';

function Stat({ label, value, tone = 'text-body' }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-line bg-bg p-6">
      <div className="text-sm text-muted">{label}</div>
      <div className={`tnum mt-2 text-2xl font-semibold ${tone}`}>{value}</div>
    </div>
  );
}

export function Ir() {
  const { t, pick, money, ratio } = useT();
  const s = useSiteData();
  const fr = useApi<FundraisingApi>('/api/group/fundraising');
  const fin = useApi<FinancialsApi>('/api/group/financials');
  const error = s.error ?? fr.error ?? fin.error;
  if (error) return <div className="mx-auto max-w-6xl px-5 py-24 text-danger-light">{error}</div>;
  if (!s.data || !fr.data || !fin.data) {
    return <div className="mx-auto max-w-6xl px-5 py-24 text-muted">{t('state.loading')}</div>;
  }
  const f = fr.data;
  const sm = fin.data.summary;
  const cap = s.data.overview.capTable;
  const img = findImage(s.data.imagery, 'skyline');

  return (
    <>
      <PageBanner
        image={assetUrl(img.local)}
        imageAlt={pick(img.alt)}
        imageCredit={creditOf(img)}
        eyebrow={t('ir.eyebrow')}
        title={t('ir.title')}
        lead={t('ir.lead')}
      />

      <Section id="round">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <SectionTitle id="round" title={`${t('ir.round.title')} · ${f.round}`} />
          <DataBadges meta={fr.meta} />
        </div>
        <dl className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label={t('ir.round.target')} value={money(f.targetAmount)} tone="text-primary-light" />
          <Stat label={t('ir.round.equity')} value={ratio(f.equityOffered)} />
          <Stat label={t('ir.round.pre')} value={money(f.preMoneyValuation)} />
          <Stat label={t('ir.round.post')} value={money(f.postMoneyValuation)} />
        </dl>
        <p className="mt-6 text-sm text-muted">
          {t('ir.round.instrument')}：{pick(f.instrument)}
        </p>
      </Section>

      <Section id="use" tone="surface">
        <SectionTitle id="use" title={t('ir.use.title')} lead={t('ir.use.lead')} />
        <ul className="mt-12 space-y-6">
          {f.useOfFunds.map((u, i) => (
            <li key={i}>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <span className="text-base font-semibold text-body">{pick(u.item)}</span>
                <span className="tnum text-sm text-body">
                  {money(u.amount)} · {ratio(u.share, 0)}
                </span>
              </div>
              <div className="mt-3 h-3 overflow-hidden rounded-full bg-line" role="img" aria-label={`${pick(u.item)} ${ratio(u.share, 0)}`}>
                <div className="h-full rounded-full bg-primary" style={{ width: `${u.share * 100}%` }} />
              </div>
              <p className="mt-2 text-sm text-muted">{pick(u.milestone)}</p>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="fin">
        <SectionTitle id="fin" title={t('ir.fin.title')} lead={t('ir.fin.lead', { year: fin.data.fiscalYear })} />
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Stat label={t('ir.fin.revenue')} value={money(sm.revenue)} tone="text-primary-light" />
          <Stat label={t('ir.fin.grossMargin')} value={ratio(sm.grossMargin)} />
          <Stat label={t('ir.fin.netProfit')} value={money(sm.netProfit)} />
          <Stat label={t('ir.fin.netMargin')} value={ratio(sm.netMargin)} />
          <Stat label={t('ir.fin.otherIncome')} value={money(sm.otherIncome)} tone="text-policy-light" />
          <Stat label={t('ir.fin.grant')} value={ratio(sm.grantShareOfNetProfit)} tone="text-warn-light" />
        </div>
        <p className="mt-6 text-sm text-muted">{t('ir.fin.note')}</p>
      </Section>

      <Section id="cap" tone="surface">
        <SectionTitle id="cap" title={t('ir.cap.title')} lead={t('ir.cap.lead')} />
        <ul className="mt-12 space-y-5">
          {cap.shareholders.map((h, i) => (
            <li key={i}>
              <div className="flex justify-between text-sm">
                <span className="text-body">{pick(h.name)}</span>
                <span className="tnum text-body">{ratio(h.share, 1)}</span>
              </div>
              <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-line" role="img" aria-label={`${pick(h.name)} ${ratio(h.share, 1)}`}>
                <div className="h-full rounded-full bg-policy" style={{ width: `${h.share * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-6 text-sm text-muted">{pick(cap.note)}</p>
      </Section>

      <Section id="history">
        <SectionTitle id="history" title={t('ir.history.title')} />
        <div className="mt-10 overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="border-b border-line-strong text-left text-muted">
              <tr>
                <th scope="col" className="py-3 font-medium">{t('ir.history.round')}</th>
                <th scope="col" className="py-3 font-medium">{t('ir.history.date')}</th>
                <th scope="col" className="py-3 text-right font-medium">{t('ir.history.amount')}</th>
                <th scope="col" className="py-3 text-right font-medium">{t('ir.history.equity')}</th>
                <th scope="col" className="py-3 text-right font-medium">{t('ir.history.post')}</th>
              </tr>
            </thead>
            <tbody className="tnum">
              {f.history.map((h, i) => (
                <tr key={i} className="border-b border-line">
                  <td className="py-3 text-body">{pick(h.round)}</td>
                  <td className="py-3 text-muted">{h.date}</td>
                  <td className="py-3 text-right text-body">{money(h.amount)}</td>
                  <td className="py-3 text-right text-body">{ratio(h.equityOffered)}</td>
                  <td className="py-3 text-right text-body">{money(h.postMoneyValuation)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="ir-contact" tone="surface">
        <SectionTitle id="ir-contact" title={t('ir.contact.title')} lead={t('ir.contact.lead')} />
        <ul className="mt-10 grid gap-4 md:grid-cols-2">
          {s.data.company.contact.channels.map((c) => (
            <li key={c.id} className="rounded-xl border border-line bg-bg p-5">
              <div className="text-xs text-muted">{pick(c.label)}</div>
              <div className="mt-1 text-base text-body">{pick(c.value)}</div>
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}
